import forEach from "core/src/core/util/for-each.js";
import * as gsf from "core/src/core/game-state-functions.js";
import * as trajectoryUtil from "core/src/core/geometry/trajectory/trajectory-util.js";

import ScaledCanvasContext from "../scaled-canvas-context.js";
import * as PowerUpImageLoader from "./powerup-image-loader.js";
import { RED_MISSILE_SPEED } from "core/src/core/constants.js";
import { drawUpright } from "../arena-view";
import { drawPowerUpAnim, getPowerUpAnimVariant, animDespawnDuration } from "../powerup-anim";
import { nextMissileSpeed } from "../missile-flight.js";

var POWERUP_SPAWN_DURATION = 0.3;
// The grab (de-spawn) animation plays over this window on the grabbing worm's clock, then
// the power-up is removed. Longer than a bare shrink so the burst has room to read.
var POWERUP_DESPAWN_DURATION = 0.4;
var POWERUP_COLORS = {
    "self": "#008000",
    "others": "#FF0B0B",
    "all": "#3453BC"
};


// `getSpawnTime` (optional): a separate clock for when power-ups APPEAR, distinct
// from the render clock used for despawns. Online, a power-up is static, so there's
// no reason to hide it behind the opponent render delay - showing it at the truth
// clock (server-now) gives full reaction time and matches your own real-time worm.
// `getWormRenderTime(trackId)` (optional): the online per-opponent adaptive render
// clock. A despawn plays on the GRABBING worm's clock so an opponent's pickup vanishes
// exactly as their (render-delayed) head reaches it; your own pickups play on the truth
// clock (real-time head). Offline / no getSpawnTime: spawnTime === renderTime, unchanged.
export default function PowerUpRenderer({ gameState, canvas, scale=1, predictor, getSpawnTime, getWormRenderTime, canvasState, localPlayerIds, rotated=false }) {

    var context = canvas.getContext("2d");
    var scaledContext = ScaledCanvasContext(context, scale);

    // The clock a power-up's despawn shrink plays on. Your own predicted pickup: the truth
    // clock (spawnTime ~ your real-time head). An opponent's: THAT worm's adaptive render
    // clock, so it vanishes as their delayed head reaches it - not on the shared frame clock,
    // which runs ahead of a laggy opponent's head and made the power-up disappear early / feel
    // mistimed. Falls back to the frame clock (offline, or no per-worm clock yet).
    function despawnClockFor(renderData, renderTime, spawnTime) {
        if (renderData.ownDespawn) return spawnTime;
        if (renderData.grabbedBy && getWormRenderTime) {
            var t = getWormRenderTime(renderData.grabbedBy);
            if (typeof t === "number") {
                // The grabber's clock can stop: at its death, and for EVERYONE at a
                // switcharoonie freeze (a frozen worm streams nothing, so its clock
                // parks at its last segment). Either way the burst must play out, so
                // once it has begun this clock never runs slower than the frame
                // clock: it follows the worm's clock while that advances, and
                // carries on at frame speed while it stalls. A backward jump (a
                // replay scrub) starts it over from the worm's clock.
                // Kept in grabClocks, by power-up id: the render data is rebuilt
                // every frame, so a clock stored on it was lost each frame and
                // the burst sat frozen on the grabber's stalled clock.
                var c = grabClocks[renderData.id];
                if (!c || renderTime < c.frame) {
                    grabClocks[renderData.id] = c = { value: t, frame: renderTime };
                } else {
                    c.value = Math.max(t, c.value + (renderTime - c.frame));
                    c.frame = renderTime;
                }
                return c.value;
            }
        }
        return renderTime;
    }

    var prevRenderTime = 0;
    var powerUpRenderData = {};
    // power-up id -> the grab burst's catch-up clock (despawnClockFor). Lives as
    // long as the renderer (a round), NOT the per-frame render data: a finished
    // burst's clock must stay too, or the next frame's rebuild would start it
    // over from the stalled worm clock and bring the burst back.
    var grabClocks = {};
    // Dirty-rect clearing: last frame's drawn regions, erased (with this frame's) instead
    // of the whole canvas. null = full clear next frame (first frame, or a time rewind).
    // Tokens, missiles and impacts cover a small fraction of the arena, so this keeps the
    // GPU from re-rasterizing the entire fullscreen layer texture every frame.
    var prevDirtyRects = null;
    // The grab/spawn FX draw outside the token circle (rings expand to ~2.6r, the spawn
    // pop overshoots), so each token's clear box is padded well past its radius.
    // Sized to the WORST-case token animation extent, not the token itself: the
    // despawn/grab variants slide the token by 3r (bottom edge 4r) and throw
    // confetti dots out to ~4.1r plus their own size - a 3.2r pad left those pixels
    // outside the cleared box, so grab bursts smeared permanent remnants.
    var TOKEN_CLEAR_PAD = 4.4;

    // Rebuild from scratch each frame (few power-up events per round): spawn events gate on
    // spawnTime (truth); despawns record their time + grabber and play on the grabber's clock
    // (despawnClockFor). Handles a backward time jump (replay scrub) with no special-casing.
    function updateRenderData(renderTime, spawnTime) {
        powerUpRenderData = {};
        var powerUpEvents = gameState.powerUpEvents;
        for (var i = 0; i < powerUpEvents.length; i++) {
            var powerUpEvent = powerUpEvents[i];
            if (powerUpEvent.type === "spawn") {
                if (powerUpEvent.time > spawnTime) continue;
                var powerUp = powerUpEvent.powerUp;
                powerUpRenderData[powerUp.id] = {
                    id: powerUp.id,
                    centerX: powerUp.shape.centerX,
                    centerY: powerUp.shape.centerY,
                    radius: powerUp.shape.radius,
                    color: POWERUP_COLORS[powerUp.affects],
                    affects: powerUp.affects,
                    name: powerUp.name,
                    spawnTime: powerUpEvent.time,
                    despawnTime: Infinity
                };
            } else if (powerUpEvent.type === "despawn") {
                // No render-clock gate here: record the despawn (time + who grabbed it) and let
                // despawnClockFor decide WHEN it plays, on the grabbing worm's clock.
                if (powerUpRenderData[powerUpEvent.id]) {
                    powerUpRenderData[powerUpEvent.id].despawnTime = powerUpEvent.time;
                    powerUpRenderData[powerUpEvent.id].grabbedBy = powerUpEvent.grabbedBy;
                }
            } else {
                throw new Error("Unknown powerUp event type: " + powerUpEvent.type);
            }
        }
        // Local predicted pickup: start the SAME despawn shrink an opponent's pickup shows,
        // but anchored to the pickup's GAME-TIME (from the predictor - stable across renderer
        // rebuilds) and, via ownDespawn below, played on the TRUTH clock. So it despawns
        // exactly when your real-time head passes it, not a render-delay later. If a server
        // despawn also lands, whichever is earlier wins.
        forEach(powerUpRenderData, function (renderData, id) {
            if (predictor && predictor.isPickedUp(id)) {
                var pt = predictor.pickupTime ? predictor.pickupTime(id) : undefined;
                if (typeof pt !== "number") pt = renderTime;
                if (pt < renderData.despawnTime) {
                    renderData.despawnTime = pt;
                    renderData.ownDespawn = true;
                }
            }
        });
        var despawnDur = animDespawnDuration(getPowerUpAnimVariant());
        forEach(powerUpRenderData, function (renderData, id) {
            if (despawnClockFor(renderData, renderTime, spawnTime) - renderData.despawnTime >= despawnDur) {
                delete powerUpRenderData[id];
            }
        });
        prevRenderTime = renderTime;
    }

    // A worm track's head position at `time`, in PIXELS (grabbedBy is a track id).
    function trackHeadPx(trackId, time) {
        if (trackId === undefined || trackId === null) return null;
        var seg = gsf.getLatestWormPathSegment(gameState, trackId);
        if (!seg) return null;
        var tt = Math.min(time, seg.endTime);
        var p = trajectoryUtil.followTrajectory(seg, tt - seg.startTime);
        return { x: p.x * scale, y: p.y * scale };
    }
    // A worm's head where it is VISIBLY DRAWN this frame, in PIXELS. Opponents: their
    // per-track adaptive render clock (fallback the frame clock). A locally PREDICTED track
    // extends past the frame clock (your own worm, drawn at its predicted tip): aim at the
    // tip. Never the newest network data ahead of the drawn head - that point advances in
    // bursts and made the red throw chase a stuttering ghost ("lagging toward the enemy").
    function drawnHeadPx(trackId, renderTime) {
        var seg = gsf.getLatestWormPathSegment(gameState, trackId);
        if (!seg) return null;
        var clockT = renderTime;
        if (getWormRenderTime) {
            var wt = getWormRenderTime(trackId);
            if (typeof wt === "number") clockT = wt;
        }
        if (seg.endTime > renderTime + 0.05) clockT = seg.endTime; // own predicted track: the drawn tip
        var tt = Math.min(clockT, seg.endTime);
        var p = trajectoryUtil.followTrajectory(seg, tt - seg.startTime);
        return { x: p.x * scale, y: p.y * scale, headR: (seg.size || 3) * scale };
    }
    // Alive worms NOT owned by the grabbing player: {trackId, x, y} at their drawn heads.
    // `ownGrab`: a locally PREDICTED pickup - the server's grabbedBy hasn't arrived yet,
    // so exclude every local player's worms (core targets only the grabber's enemies;
    // without this the client aimed a phantom missile at the grabber's own head).
    function enemyEntries(grabTrackId, renderTime, ownGrab) {
        var grabPlayerId = null;
        if (grabTrackId !== undefined && grabTrackId !== null) {
            var g = gsf.getLatestWormPathSegment(gameState, grabTrackId);
            if (g) grabPlayerId = g.playerId;
        }
        var out = [];
        for (var trackId in gameState.wormPathSegments) {
            if (trackId.indexOf("#") !== -1) continue; // wall-hack ghost stubs are not worms
            var seg = gsf.getLatestWormPathSegment(gameState, trackId);
            if (!seg) continue;
            if (grabPlayerId !== null && seg.playerId === grabPlayerId) continue;
            if (ownGrab && localPlayerIds && localPlayerIds.indexOf(seg.playerId) !== -1) continue;
            var worm = gsf.getWorm(gameState, seg.wormId);
            if (!worm || !worm.alive) continue;
            var h = drawnHeadPx(trackId, renderTime);
            if (h) out.push({ trackId: trackId, wormId: seg.wormId, x: h.x, y: h.y });
        }
        return out;
    }
    // Worm targets for the directional grab animations (green sucks to the grabber; red
    // streak variants 41-53 aim at drawn heads). Guarded so odd state never crashes.
    function grabTargets(renderData, time, renderTime) {
        try {
            if (renderData.affects === "self") return { selfTarget: trackHeadPx(renderData.grabbedBy, renderData.ownDespawn ? Infinity : time) };
            if (renderData.affects === "others") return { enemyTargets: enemyEntries(renderData.grabbedBy, renderTime, renderData.ownDespawn && renderData.grabbedBy == null) };
        } catch (e) { /* fall back to a non-directional burst */ }
        return undefined;
    }

    // ===== RED homing missiles (variants 54-69) =====
    // The missile is REAL: core simulates each red delivery pursuing its victim's live
    // head at RED_MISSILE_SPEED and applies the debuff on the ACTUAL catch, so fleeing a
    // missile genuinely buys time (see core activatePowerUp + updateEffects). This
    // renderer shows that pursuit two ways:
    //  - LIVE LOCAL play: gameState.missiles IS the truth (core mutates it every tick) -
    //    mirror it 1:1 into the visual list (syncCoreMissiles adds trails + bursts).
    //  - ONLINE (and replays, which have no live missiles array): the missiles are not
    //    streamed, so each is flown here, homing on its victim's DRAWN head. Two things
    //    have to hold at once: it must MOVE smoothly, and it must LAND when the streamed
    //    effect (fatten/slow + timer ring) shows on the victim.
    //    It used to step by the victim's own render clock, which reproduces core's
    //    pursuit exactly but moves like that clock does: an opponent's clock is an
    //    adaptive, per-connection one that holds and catches up all the time, and the
    //    missile visibly stuttered with it. So the missile now flies on the VIEW's
    //    steady clock, and only its SPEED keeps time (missile-flight.js): it follows
    //    the victim's clock smoothly, and once the landing moment is known (the
    //    effect's spawn event carries it, and arrives a render delay before the
    //    victim's clock gets there) it covers what is left in the time that is left.
    var MISSILE_SPEED = RED_MISSILE_SPEED; // game-px per second (worms move at ~100)
    var MISSILE_TRAIL = 7;      // recent positions kept for the fading tail
    var IMPACT_DURATION = 0.3;  // seconds the landing burst plays
    var MISSILE_MAX_FLIGHT = 8; // seconds of the view's time; nothing real flies this long
    // Missile state lives on the per-view canvasState (fallback: private) so it survives
    // renderer rebuilds (e.g. a switcharoonie changing the predicted-track set mid-flight)
    // and stays per view - each canvas has its own missiles and landing times.
    var fxHost = canvasState || {};
    // Keyed to THIS round's gameState: a new round (or replay selection) swaps the
    // gameState object, and its power-up / track ids restart from scratch, so stale
    // state would misfire - last round's spawnedFor ids block this round's launches
    // (a grab with no projectiles), and last round's in-flight missiles would open the
    // new round shooting at the reused track names. Mid-round renderer rebuilds (twin,
    // switcharoonie, resize) keep the same gameState, so in-flight state survives those.
    if (!fxHost.missileFx || fxHost.missileFx.gs !== gameState) {
        fxHost.missileFx = { gs: gameState, missiles: [], impacts: [], spawnedFor: {}, claimed: {} };
    }
    var missiles = fxHost.missileFx.missiles;
    var impacts = fxHost.missileFx.impacts;
    // True while gameState.missiles is the live source (a local game core is simulating);
    // set per frame in render(). Online snapshots carry a STALE missiles array (deltas
    // never update it) and replay states have none - both run the client-side pursuit.
    var useCoreMissiles = false;

    function spawnMissiles(renderData, renderTime, id) {
        var startX = renderData.centerX * scale, startY = renderData.centerY * scale;
        var ownGrab = !!renderData.ownDespawn && renderData.grabbedBy == null;
        enemyEntries(renderData.grabbedBy, renderTime, ownGrab).forEach(function (en) {
            missiles.push({
                x: startX, y: startY, tx: en.x, ty: en.y, hitR: 4,
                targetTrackId: en.trackId, targetWormId: en.wormId, color: renderData.color,
                r: renderData.radius * scale, trail: [], puId: id,
                // To find its landing in the effect stream: the debuff it carries is
                // named after the power-up and spawns on the victim after the grab.
                name: renderData.name, launchTime: renderData.despawnTime,
                landTime: undefined, speed: 1, lastFrame: undefined, bornAt: undefined,
                clock0: undefined, flown: 0, // the victim's clock at launch; flight time flown
            });
        });
    }

    // The game time this missile lands at, once the host has said so: the spawn event of
    // the effect it carries (same name, on its victim, after the grab). Each event is
    // claimed by one missile, so two of the same power-up in the air stay apart.
    function findLandTime(m) {
        var events = gameState.effectEvents || [];
        var claimed = fxHost.missileFx.claimed || (fxHost.missileFx.claimed = {});
        for (var i = events.length - 1; i >= 0; i--) {
            var ev = events[i];
            if (ev.time < (m.launchTime || 0) - 0.05) break; // events are in time order
            if (ev.type !== "spawn" || !ev.effect || ev.effect.wormId !== m.targetWormId || ev.effect.name !== m.name) continue;
            var key = ev.effect.id !== undefined ? ev.effect.id : ev.time + ":" + ev.effect.wormId;
            if (claimed[key] !== undefined && claimed[key] !== m.puId) continue;
            claimed[key] = m.puId;
            return ev.time;
        }
        return undefined;
    }

    function updateMissiles(renderTime) {
        for (var i = missiles.length - 1; i >= 0; i--) {
            var m = missiles[i];
            if (m.coreId !== undefined) { missiles.splice(i, 1); continue; } // stale core mirror (source switched)
            var tgt = drawnHeadPx(m.targetTrackId, renderTime);
            if (tgt) { m.tx = tgt.x; m.ty = tgt.y; m.hitR = tgt.headR; }
            // The victim's own render clock: what its drawn head, its death and the
            // landing moment are all timed on.
            var clock = renderTime;
            if (getWormRenderTime) {
                var wc = getWormRenderTime(m.targetTrackId);
                if (typeof wc === "number") clock = wc;
            }
            // The view's steady time since the last frame (0 when a replay is paused or
            // scrubbed backwards; a long stall is not flown through in one jump).
            var dt = m.lastFrame === undefined ? 0 : Math.max(0, Math.min(0.1, renderTime - m.lastFrame));
            m.lastFrame = renderTime;
            if (m.bornAt === undefined || renderTime < m.bornAt) m.bornAt = renderTime;
            // A victim that dies mid-flight fizzles its missile, like core does. Read off
            // the track's marker, not worm.alive: a victory-lap crash marks nobody dead.
            // The slack covers the wire rounding segment times (0.1ms).
            var victimSeg = gsf.getLatestWormPathSegment(gameState, m.targetTrackId);
            var victimGone = !victimSeg || (victimSeg.type === "worm_died" && clock >= victimSeg.startTime - 0.001);
            if (victimGone || renderTime - m.bornAt > MISSILE_MAX_FLIGHT) {
                impacts.push({ x: m.x, y: m.y, t0: renderTime, color: m.color, r: m.r });
                missiles.splice(i, 1);
                continue;
            }
            var dx = m.tx - m.x, dy = m.ty - m.y;
            var dist = Math.hypot(dx, dy) || 1;
            var reach = Math.max(m.hitR, 2);
            // Speed, as a multiple of core's (missile-flight.js): what covers the distance
            // left in the time left, once the landing moment is known.
            if (m.landTime === undefined) m.landTime = findLandTime(m);
            if (m.clock0 === undefined || clock < m.clock0) { m.clock0 = clock; m.flown = 0; }
            m.speed = nextMissileSpeed(m.speed, m.landTime, clock, dist - reach, MISSILE_SPEED * scale, dt, (clock - m.clock0) - m.flown);
            m.flown += m.speed * dt;
            var step = MISSILE_SPEED * scale * m.speed * dt;
            if (dist <= step + reach) {
                impacts.push({ x: m.tx, y: m.ty, t0: renderTime, color: m.color, r: m.r });
                missiles.splice(i, 1);
                continue;
            }
            if (dt > 0) {
                m.trail.unshift({ x: m.x, y: m.y });
                if (m.trail.length > MISSILE_TRAIL) m.trail.pop();
            }
            m.x += (dx / dist) * step;
            m.y += (dy / dist) * step;
        }
    }

    // LIVE LOCAL play: mirror core's simulated missiles 1:1 (positions are truth); this
    // side only adds the comet trail and the landing burst when one disappears.
    function syncCoreMissiles(renderTime) {
        var core = gameState.missiles || [];
        var seen = {};
        for (var i = 0; i < core.length; i++) {
            var cm = core[i];
            seen[cm.id] = true;
            var m = null;
            for (var j = 0; j < missiles.length; j++) {
                if (missiles[j].coreId === cm.id) { m = missiles[j]; break; }
            }
            if (!m) {
                m = { coreId: cm.id, x: cm.x * scale, y: cm.y * scale, color: POWERUP_COLORS.others, r: (cm.radius || 10) * scale, trail: [] };
                missiles.push(m);
            } else {
                m.trail.unshift({ x: m.x, y: m.y });
                if (m.trail.length > MISSILE_TRAIL) m.trail.pop();
            }
            m.x = cm.x * scale;
            m.y = cm.y * scale;
        }
        // Gone from core = landed (burst) or fizzled with its victim (burst on the corpse).
        for (var k = missiles.length - 1; k >= 0; k--) {
            if (missiles[k].coreId !== undefined && !seen[missiles[k].coreId]) {
                impacts.push({ x: missiles[k].x, y: missiles[k].y, t0: renderTime, color: missiles[k].color, r: missiles[k].r });
                missiles.splice(k, 1);
            }
        }
    }

    function drawMissiles(renderTime) {
        for (var i = 0; i < missiles.length; i++) {
            var m = missiles[i];
            // The light comet look (matches the original #69 red streak): one thin coloured
            // line through the recent positions + a small white-hot tip. The path bends as
            // the missile homes, so the streak curves naturally.
            var lw = Math.max(0.75, m.r * 0.1);
            context.lineCap = "round";
            context.strokeStyle = m.color;
            context.lineWidth = lw;
            context.beginPath();
            context.moveTo(m.x, m.y);
            for (var k = 0; k < m.trail.length; k++) {
                context.lineTo(m.trail[k].x, m.trail[k].y);
            }
            context.stroke();
            context.globalAlpha = 0.9;
            context.fillStyle = "#ffffff";
            context.beginPath();
            context.arc(m.x, m.y, lw * 0.7, 0, Math.PI * 2);
            context.fill();
            context.globalAlpha = 1;
        }
        // Impact bursts: expanding ring + dots at the landing spot (mirrors the variant look).
        for (var j = impacts.length - 1; j >= 0; j--) {
            var im = impacts[j];
            var ip = (renderTime - im.t0) / IMPACT_DURATION;
            if (ip >= 1 || ip < 0) { impacts.splice(j, 1); continue; }
            context.globalAlpha = 1 - ip;
            context.strokeStyle = im.color;
            context.lineWidth = Math.max(0.75, im.r * 0.14 * (1 - ip));
            context.beginPath();
            context.arc(im.x, im.y, im.r * (0.2 + ip), 0, Math.PI * 2);
            context.stroke();
            context.fillStyle = im.color;
            for (var k2 = 0; k2 < 6; k2++) {
                var a = (k2 / 6) * Math.PI * 2, dd = im.r * (0.2 + ip * 0.9);
                context.beginPath();
                context.arc(im.x + Math.cos(a) * dd, im.y + Math.sin(a) * dd, Math.max(0.5, im.r * 0.09 * (1 - ip)), 0, Math.PI * 2);
                context.fill();
            }
            context.globalAlpha = 1;
        }
    }

    function renderPowerUps(renderTime, spawnTime) {
        var animVariant = getPowerUpAnimVariant();
        var directional = animVariant >= 41 && animVariant <= 69;
        var despawnDur = animDespawnDuration(animVariant);
        forEach(powerUpRenderData, function (renderData, id) {
            // NOTE: a locally (predictively) picked-up power-up is NOT hidden here anymore;
            // updateRenderData gives it a despawnTime so it plays the grab animation below,
            // the same as an opponent's pickup.
            // Spawn progress (grow-in) runs on the spawn (truth) clock. Grab progress (the
            // de-spawn animation) runs on the grabbing worm's clock: the truth clock for your
            // own predicted pickups, that opponent's adaptive clock for theirs - so it fires
            // exactly as the head that took it reaches it.
            var despawnClock = despawnClockFor(renderData, renderTime, spawnTime);
            var spawnP = Math.max(0, Math.min(1, (spawnTime - renderData.spawnTime) / POWERUP_SPAWN_DURATION));
            var despawnP = 0;
            if (despawnClock > renderData.despawnTime) {
                despawnP = Math.max(0, Math.min(1, (despawnClock - renderData.despawnTime) / despawnDur));
            }

            // drawPowerUpAnim wraps the chosen token design (?pu) with the chosen spawn/grab
            // animation (?pa). Drawn in PIXELS (centre*scale, radius*scale) so the design code
            // is scale-agnostic; it guards on the glyph being decoded so it never throws.
            // Missile-family variants (54-69): red is handled by the stateful homing missiles
            // (spawned once, below) - enemyTargets is withheld so the variant's built-in
            // stateless missile (which rubber-bands against moving targets) draws nothing and
            // the token just plays its vanish puff.
            var missileStyle = animVariant >= 54 && animVariant <= 69;
            var opts;
            if (directional && despawnP > 0) {
                if (renderData.affects === "others" && missileStyle) {
                    // Core-backed: the missiles arrive via syncCoreMissiles; spawning a
                    // client copy on top would double them.
                    if (!useCoreMissiles && !fxHost.missileFx.spawnedFor[id]) {
                        fxHost.missileFx.spawnedFor[id] = true;
                        try { spawnMissiles(renderData, renderTime, id); } catch (e) { /* visual only */ }
                    }
                } else {
                    opts = grabTargets(renderData, despawnClock, renderTime);
                }
            }
            var powerUpImage = PowerUpImageLoader.getPowerUpImage(renderData.name);
            // The icon stands upright on screen even when the view is turned (arena-view.ts).
            drawUpright(context, renderData.centerX * scale, renderData.centerY * scale, rotated, function () {
                drawPowerUpAnim(
                    context,
                    renderData.centerX * scale,
                    renderData.centerY * scale,
                    renderData.radius * scale,
                    powerUpImage,
                    renderData.affects,
                    animVariant,
                    renderTime,
                    spawnP,
                    despawnP,
                    opts
                );
            });
        });
    }

    function render(renderTime) {
        var spawnTime = getSpawnTime ? getSpawnTime() : renderTime;
        if (renderTime !== prevRenderTime) {
            // Rewind (replay scrub / new round): in-flight missiles and their bookkeeping
            // belong to a future that no longer exists - drop them, and wipe the whole
            // layer once (stale pixels may sit outside any tracked rect).
            if (renderTime < prevRenderTime) {
                missiles.length = 0;
                impacts.length = 0;
                fxHost.missileFx.spawnedFor = {};
                prevDirtyRects = null;
            }
            updateRenderData(renderTime, spawnTime);
            // Advance missiles BEFORE computing clear rects so the boxes cover where
            // they'll be drawn this frame. (A missile spawned by a grab during
            // renderPowerUps below is drawn at its token's position, inside that
            // token's padded box, and gets its own rect from next frame on.)
            // Live local play (no online spawn clock, an active core game with a missiles
            // array): mirror core's simulated missiles. Everything else - online, and
            // replay states (inactive / no array) - runs the client-side pursuit.
            useCoreMissiles = !getSpawnTime && !!gameState.missiles && gameState.gameActive !== false;
            if (useCoreMissiles) syncCoreMissiles(renderTime);
            else updateMissiles(renderTime);
            var rects = [];
            forEach(powerUpRenderData, function (rd) {
                var r = rd.radius * scale * TOKEN_CLEAR_PAD + 6;
                rects.push([rd.centerX * scale - r, rd.centerY * scale - r, r * 2, r * 2]);
            });
            for (var mi = 0; mi < missiles.length; mi++) {
                var m = missiles[mi];
                var minX = m.x, maxX = m.x, minY = m.y, maxY = m.y;
                for (var k = 0; k < m.trail.length; k++) {
                    var p = m.trail[k];
                    if (p.x < minX) minX = p.x; if (p.x > maxX) maxX = p.x;
                    if (p.y < minY) minY = p.y; if (p.y > maxY) maxY = p.y;
                }
                var mpad = m.r * 0.2 + 6;
                rects.push([minX - mpad, minY - mpad, maxX - minX + mpad * 2, maxY - minY + mpad * 2]);
            }
            for (var ii = 0; ii < impacts.length; ii++) {
                var im = impacts[ii];
                var ipad = im.r * 2.4 + 4;
                rects.push([im.x - ipad, im.y - ipad, ipad * 2, ipad * 2]);
            }
            // Erase last frame's pixels + pre-clear this frame's boxes, or fall back to a
            // full wipe when there's no history or the rect count stops being a win.
            if (!prevDirtyRects || prevDirtyRects.length + rects.length > 60) {
                context.clearRect(0, 0, canvas.width, canvas.height);
            } else {
                for (var ci = 0; ci < prevDirtyRects.length; ci++) {
                    var c0 = prevDirtyRects[ci];
                    context.clearRect(c0[0], c0[1], c0[2], c0[3]);
                }
                for (var cj = 0; cj < rects.length; cj++) {
                    var c1 = rects[cj];
                    context.clearRect(c1[0], c1[1], c1[2], c1[3]);
                }
            }
            renderPowerUps(renderTime, spawnTime);
            drawMissiles(renderTime);
            prevDirtyRects = rects;
        }
    }

    return {
        render: render
    };
};
